import { ethers } from "ethers";
import type { JoinSplitNote } from "../joinsplit/types";
import { hashMpkHex } from "../utils/format";
import {
  INBOX_ECDH_PREFIX,
  encryptInboxNoteWithPublicKey,
  normalizeInboxPublicKeyHex,
} from "./inbox-crypto";

export const RECOVERY_KEY_DOMAIN = "stealthos:recovery-key:v1:";
export const RECOVERY_KEY_DOMAIN_V2 = "stealthos:recovery-key:v2:";
export const RECOVERY_NOTE_PAYLOAD_VERSION = 1;

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

function normalizeRecipientKey(recipientKey: string): string {
  return recipientKey.trim().toLowerCase().replace(/^0x/, "");
}

function normalizeBytes32Hex(value: string, label: string): string {
  const normalized = value.trim().toLowerCase();
  const hex = normalized.startsWith("0x") ? normalized : `0x${normalized}`;
  if (!/^0x[0-9a-f]{64}$/.test(hex)) {
    throw new Error(`Invalid ${label}`);
  }
  return hex;
}

function normalizeFixedHex(
  value: string,
  bytes: number,
  label: string,
): string {
  const normalized = value.trim().toLowerCase();
  const hex = normalized.startsWith("0x") ? normalized : `0x${normalized}`;
  const expectedLength = bytes * 2;
  const re = new RegExp(`^0x[0-9a-f]{${expectedLength}}$`);
  if (!re.test(hex)) {
    throw new Error(`Invalid ${label}`);
  }
  return hex;
}

async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", toArrayBuffer(bytes));
  return Buffer.from(new Uint8Array(digest)).toString("hex");
}

export async function deriveRecoveryKeyFromRecipientKey(
  recipientKey: string,
): Promise<string> {
  const normalizedRecipientKey = normalizeRecipientKey(recipientKey);
  if (!/^[0-9a-f]{64}$/.test(normalizedRecipientKey)) {
    throw new Error("Invalid recipient key");
  }
  const digest = await sha256Hex(
    `${RECOVERY_KEY_DOMAIN}${normalizedRecipientKey}`,
  );
  return `0x${digest}`;
}

export async function deriveRecoveryKeyForCommitmentFromRecipientKey(
  recipientKey: string,
  commitment: string,
): Promise<string> {
  const normalizedRecipientKey = normalizeRecipientKey(recipientKey);
  if (!/^[0-9a-f]{64}$/.test(normalizedRecipientKey)) {
    throw new Error("Invalid recipient key");
  }
  const normalizedCommitment = normalizeBytes32Hex(commitment, "commitment");
  const digest = await sha256Hex(
    `${RECOVERY_KEY_DOMAIN_V2}${normalizedRecipientKey}:${normalizedCommitment}`,
  );
  return `0x${digest}`;
}

export async function deriveRecoveryKeyFromMpkHex(
  mpkHex: string,
  commitment?: string,
): Promise<string> {
  const recipientKey = await hashMpkHex(normalizeBytes32Hex(mpkHex, "mpk"));
  if (commitment) {
    return deriveRecoveryKeyForCommitmentFromRecipientKey(
      recipientKey,
      commitment,
    );
  }
  return deriveRecoveryKeyFromRecipientKey(recipientKey);
}

export async function buildRecoveryNotePayload(
  note: JoinSplitNote,
  inboxPublicKeyHex: string,
): Promise<string> {
  const normalizedInboxPublicKey =
    normalizeInboxPublicKeyHex(inboxPublicKeyHex);
  if (!normalizedInboxPublicKey) {
    throw new Error("Invalid inbox public key");
  }

  const encrypted = await encryptInboxNoteWithPublicKey(
    note,
    normalizedInboxPublicKey,
  );
  const ephemeralPubKey = normalizeBytes32Hex(
    encrypted.ephemeralPubKey.startsWith(INBOX_ECDH_PREFIX)
      ? encrypted.ephemeralPubKey.slice(INBOX_ECDH_PREFIX.length)
      : encrypted.ephemeralPubKey,
    "ephemeral public key",
  );

  return ethers.AbiCoder.defaultAbiCoder().encode(
    ["uint8", "bytes32", "bytes12", "bytes16", "bytes"],
    [
      RECOVERY_NOTE_PAYLOAD_VERSION,
      ephemeralPubKey,
      normalizeFixedHex(encrypted.iv, 12, "iv"),
      normalizeFixedHex(encrypted.authTag, 16, "auth tag"),
      `0x${encrypted.ciphertext}`,
    ],
  );
}
